import React, { useState } from 'react';
import { useSmithing } from '../hooks/useSmithing';
import { useDraggablePanel } from '../utils/panelDragUtils';
import '../styles/Smithing.css';

interface SmithingPanelProps {
  socketRef: React.MutableRefObject<any>;
  isOpen: boolean;
  onClose: () => void;
}

export const SmithingPanel: React.FC<SmithingPanelProps> = ({ socketRef, isOpen, onClose }) => {
  const { smithingState, recipes, startSmithing, stopSmithing, isLoading, error } =
    useSmithing(socketRef);
  const { handleHeaderMouseDown, style: dragStyle, panelRef } = useDraggablePanel(200, 150);

  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  if (!isOpen) return null;

  const selectedRecipe = recipes.find(r => r.id === selectedRecipeId);
  const xpPercent = smithingState.nextLevelXp > 0
    ? ((smithingState.xp / smithingState.nextLevelXp) * 100)
    : 0;

  return (
    <div className="smithing-panel" ref={panelRef} style={dragStyle}>
      <div className="smithing-panel__header" onMouseDown={handleHeaderMouseDown}>
        <h2>Smithing</h2>
        <button className="smithing-panel__close" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Skill Stats */}
      <div className="smithing-panel__stats">
        <div className="smithing-stats">
          <div className="smithing-stat">
            <span className="smithing-stat__label">Level</span>
            <span className="smithing-stat__value">{smithingState.level}</span>
          </div>
          <div className="smithing-stat">
            <span className="smithing-stat__label">XP</span>
            <span className="smithing-stat__value">{smithingState.xp.toLocaleString()}</span>
          </div>
        </div>

        {/* XP Progress Bar */}
        <div className="smithing-progress">
          <div className="smithing-progress__bar">
            <div className="smithing-progress__fill" style={{ width: `${xpPercent}%` }} />
          </div>
          <div className="smithing-progress__text">
            {smithingState.xp.toLocaleString()} / {smithingState.nextLevelXp.toLocaleString()} XP
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="smithing-error">{error}</div>}

      {/* Current Smithing Status */}
      {smithingState.isSmithing && smithingState.currentRecipe && (
        <div className="smithing-status">
          <div className="smithing-status__recipe">{smithingState.currentRecipe.name}</div>
          <div className="smithing-status__progress">
            {smithingState.itemsSmithed} / {smithingState.batchSize} items
          </div>
          <button className="smithing-stop-btn" onClick={stopSmithing}>
            Stop Smithing
          </button>
        </div>
      )}

      {/* Recipe Selection */}
      {!smithingState.isSmithing && (
        <div className="smithing-recipes">
          <h3>Available Recipes</h3>
          {isLoading ? (
            <div className="smithing-loading">Loading recipes...</div>
          ) : recipes.length === 0 ? (
            <div className="smithing-no-recipes">No recipes available at your level</div>
          ) : (
            <div className="smithing-recipe-list">
              {recipes.map(recipe => (
                <div
                  key={recipe.id}
                  className={`smithing-recipe ${selectedRecipeId === recipe.id ? 'active' : ''}`}
                  onClick={() => setSelectedRecipeId(recipe.id)}
                >
                  <div className="smithing-recipe__name">{recipe.name}</div>
                  <div className="smithing-recipe__meta">
                    <span className="smithing-recipe__level">Lvl {recipe.requiredLevel}</span>
                    <span className="smithing-recipe__xp">{recipe.baseXp} XP</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recipe Details & Start Smithing */}
          {selectedRecipe && (
            <div className="smithing-recipe-details">
              <h4>{selectedRecipe.name}</h4>
              <p className="smithing-recipe-description">{selectedRecipe.description}</p>

              <div className="smithing-recipe-info">
                <div className="smithing-info-row">
                  <span>Required Level:</span>
                  <strong>{selectedRecipe.requiredLevel}</strong>
                </div>
                <div className="smithing-info-row">
                  <span>Smith Time:</span>
                  <strong>{(selectedRecipe.baseSmithTime / 1000).toFixed(1)}s</strong>
                </div>
                <div className="smithing-info-row">
                  <span>XP per Item:</span>
                  <strong>{selectedRecipe.baseXp}</strong>
                </div>
                {selectedRecipe.canBurn && (
                  <div className="smithing-info-row">
                    <span>Max Burn Rate:</span>
                    <strong>{(selectedRecipe.baseBurnRate * 100).toFixed(0)}%</strong>
                  </div>
                )}
              </div>

              {/* Quantity Input */}
              <div className="smithing-quantity">
                <label htmlFor="smithing-quantity">Quantity:</label>
                <input
                  id="smithing-quantity"
                  type="number"
                  min="1"
                  max="99"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>

              {/* Start Smithing Button */}
              <button
                className="smithing-start-btn"
                onClick={() => {
                  startSmithing(selectedRecipe.id, quantity);
                  setSelectedRecipeId(null);
                }}
                disabled={smithingState.level < selectedRecipe.requiredLevel}
              >
                Start Smithing ({quantity}x)
              </button>

              {smithingState.level < selectedRecipe.requiredLevel && (
                <div className="smithing-level-requirement">
                  Requires Level {selectedRecipe.requiredLevel}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
